import { Notification } from "@/utils/notificationUtils";
import { openBlobFile } from "@/utils/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { commissionPromotionApi } from "../../../../modules/commissions/promotions/api";
import { CommissionPromotionType } from "../../../../modules/commissions/promotions/enums";
import {
  CommissionPromotionFilterExportRequest,
  CommissionPromotionFilterPageRequest,
  UpdateCommissionPromotion,
} from "../../../../modules/commissions/promotions/types";
import { TwoLevelEntityIdObject, TwoLevelEntityObject } from "../../../types";
import { COMMISSION_PROMOTIONS_QUERY_KEYS } from "./queryKeys";

export const useFilterCommissionPromotions = (payload: CommissionPromotionFilterPageRequest) => {
  return useQuery({
    queryKey: COMMISSION_PROMOTIONS_QUERY_KEYS.filterCommissionPromotions(payload),
    queryFn: async () => {
      const { data } = await commissionPromotionApi.filterCommissionPromotions(payload);

      return data;
    },
  });
};

export const useApproveAllPendingCommissionPromotions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSION_PROMOTIONS_QUERY_KEYS.approveAllPendingCommissionPromotions(),
    mutationFn: async (type: CommissionPromotionType) => {
      const { data } = await commissionPromotionApi.approveAllPendingCommissionPromotions({ type });

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: COMMISSION_PROMOTIONS_QUERY_KEYS.filterCommissionPromotions() });
      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("common.updateOperationSuccessPlural", { count: data }),
      });
    },
  });
};

export const useUpdateCommissionPromotion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSION_PROMOTIONS_QUERY_KEYS.updateCommissionPromotion(),
    mutationFn: async (payload: TwoLevelEntityObject<UpdateCommissionPromotion>) => {
      const { data } = await commissionPromotionApi.updateCommissionPromotion(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMISSION_PROMOTIONS_QUERY_KEYS.filterCommissionPromotions() });
      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("common.updateOperationSuccess"),
      });
    },
  });
};

export const useDeleteCommissionPromotion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSION_PROMOTIONS_QUERY_KEYS.deleteCommissionPromotion(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return commissionPromotionApi.deleteCommissionPromotion(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMMISSION_PROMOTIONS_QUERY_KEYS.filterCommissionPromotions() });
      Notification.itemDeletedNotification();
    },
  });
};

export const useDownloadCommissionPromotionsExport = () => {
  return useMutation({
    mutationKey: COMMISSION_PROMOTIONS_QUERY_KEYS.downloadCommissionPromotionsExport(),
    mutationFn: async (payload: CommissionPromotionFilterExportRequest) => {
      const response = await commissionPromotionApi.downloadCommissionPromotionsExport(payload);
      openBlobFile(response, true);

      return response.data;
    },
  });
};
